import { AppError } from '@/lib/errors'
import type { VacationsApi } from './api'

/**
 * Real API client (Hono on Cloudflare Workers). Every request carries the user's
 * Supabase JWT; the worker verifies it and forwards it to Supabase, so RLS applies.
 * Route list and payloads: docs/API.md.
 */
export function createHttpApi(baseUrl: string, token: () => string | null): VacationsApi {
  async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const t = token()
    let res: Response
    try {
      res = await fetch(`${baseUrl}/v1${path}`, {
        method,
        headers: {
          'content-type': 'application/json',
          ...(t ? { authorization: `Bearer ${t}` } : {}),
        },
        body: body === undefined ? undefined : JSON.stringify(body),
      })
    } catch {
      throw new AppError('Sem ligação ao servidor. Verifique a sua ligação e tente novamente.', 'network')
    }
    if (res.status === 204) return undefined as T
    const data = (await res.json().catch(() => ({}))) as { error?: string; code?: string }
    if (!res.ok) {
      const kind =
        res.status === 401 ? 'auth' : res.status === 403 ? 'permission' : res.status === 404 ? 'not_found' : res.status === 409 ? 'conflict' : res.status === 422 || res.status === 400 ? 'validation' : 'unknown'
      throw new AppError(data.error ?? 'Ocorreu um erro inesperado. Tente novamente.', kind)
    }
    return data as T
  }
  const o = (orgId: string) => `/orgs/${encodeURIComponent(orgId)}`

  return {
    memberships: () => call('GET', '/me/memberships'),
    createOrganization: (input) => call('POST', '/orgs', input),
    workspace: (orgId) => call('GET', `${o(orgId)}/workspace`),
    activity: (orgId) => call('GET', `${o(orgId)}/activity`),
    createRequest: (orgId, input) => call('POST', `${o(orgId)}/requests`, input),
    cancelRequest: (orgId, id) => call('POST', `${o(orgId)}/requests/${id}/cancel`),
    decideRequest: (orgId, id, decision, comment) => call('POST', `${o(orgId)}/requests/${id}/decision`, { decision, comment }),
    createChange: (orgId, input) => call('POST', `${o(orgId)}/change-requests`, input),
    decideChange: (orgId, id, decision, comment) => call('POST', `${o(orgId)}/change-requests/${id}/decision`, { decision, comment }),
    saveEmployee: (orgId, input) => (input.id ? call('PUT', `${o(orgId)}/employees/${input.id}`, input) : call('POST', `${o(orgId)}/employees`, input)),
    saveDepartment: (orgId, input) => (input.id ? call('PUT', `${o(orgId)}/departments/${input.id}`, input) : call('POST', `${o(orgId)}/departments`, input)),
    saveBalanceRecord: (orgId, input) => call('PUT', `${o(orgId)}/balances/${input.employeeId}/${input.year}`, input),
    saveCustomDay: (orgId, input) => (input.id ? call('PUT', `${o(orgId)}/custom-days/${input.id}`, input) : call('POST', `${o(orgId)}/custom-days`, input)),
    deleteCustomDay: (orgId, id) => call('DELETE', `${o(orgId)}/custom-days/${id}`),
    updateSettings: (orgId, settings, name) => call('PUT', `${o(orgId)}/settings`, { settings, name }),
  }
}
